/**
 * brand-bar — the storefront's page chrome, one object for every route that
 * shows it: the workspace mark that leads home, the way back to this
 * browser's own orders, and the language toggle. All three are chrome rather
 * than content, so they resolve from the workspace card, the buyer's own
 * history and the locale — never from whatever event or order the route
 * names — and they render in every frame a route has, loading and not-found
 * included: a visitor who followed a dead link still has the way home and
 * their own language.
 *
 * The orders entry is drawn for a browser that has bought something, and for
 * one whose listener failed. Landing a buyer with nothing on a page that says
 * so is a dead end offered as a destination, so the entry states that truth by
 * being absent — but not-knowing is not that truth: once the entry is the only
 * link to the list, hiding it on a failed query is the one state that leaves
 * the buyer no way to the error frame written for them. /orders stays
 * deep-linkable throughout, and carries the empty state for a cold cache.
 *
 * The bar can be told the buyer already holds tickets for the event on the
 * page, and answers once per mount. That fact, wherever the orders entry is
 * drawn, puts the sentence saying so into a polite live region beside the
 * entry — announcement only, never a second way in. A reader who has not
 * asked for reduced motion gets the library's hummingbird alongside it,
 * flying to that same entry with that same sentence as its caption; a reader
 * who has asked for reduced motion gets the sentence and no flight.
 *
 * The mark is height-constrained to sit in the toggle's own band
 * (brand-logo.view.ts derives its width from that height), so the pair reads
 * as one row rather than a short mark floating beside a taller control.
 * Like `<language-toggle>`, this renders only the row — each host supplies
 * its own placement (the event header's static top strip, order-access's
 * sticky bar), since the padding and stickiness belong to the page, not to
 * the chrome itself.
 */
import { area } from "@mhmo91/schmancy";
import { hummingbird, reducedMotion$ } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { organizationPathPrefix } from "../workspace/organization.state.ts";
// Side-effect: registers <schmancy-visually-hidden>.
import "@mhmo91/schmancy/visually-hidden";
import { css, html, type PropertyValues } from "lit";
import { property, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../i18n/localize.ts";
import { openBrandName } from "../workspace/workspace.state.ts";
import "../workspace/brand-logo.view.ts";
import "./language-toggle.view.ts";
import { hasOrders, orderHistory } from "./orders/orders.state.ts";

@schmancyElement("brand-bar")
export class BrandBar extends LocalizeMixin(SchmancyElement) {
	// Block, not the inline default: the row below is a block-level flex
	// container, and an inline host would leave its placement to the host
	// page's text flow rather than its own layout.
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	];

	/** Raised by the page when this browser already holds tickets for the event
	 *  it shows. The order page renders this bar too and never raises it, so
	 *  the flourish never fires there. */
	@property({ type: Boolean }) ticketsHere = false;

	/** One flight per mount: `flying` is what the directive's `playing` reads,
	 *  and `flown` is terminal, so no later re-render can ask for a second. */
	@state() private flourish: "waiting" | "flying" | "flown" = "waiting";

	override willUpdate(changed: PropertyValues) {
		super.willUpdate(changed);
		// The entry is the flight's whole subject: there is nothing to visit until
		// it is drawn, and a bird already in the air loses the node that reports
		// its own completion the moment the entry goes.
		const entryDrawn = hasOrders.value || orderHistory.value.status === "error";
		if (entryDrawn && this.flourish === "waiting" && this.ticketsHere) {
			// The directive damps its own wing blur and hover, not the flight across
			// the page, so reduced motion is answered here: settle on the terminal
			// state, which leaves the live region below as the whole answer.
			this.flourish = reducedMotion$.value ? "flown" : "flying";
		} else if (!entryDrawn && this.flourish === "flying") {
			this.flourish = "flown";
		}
	}

	private toStorefront() {
		area.push({
			area: "root",
			component: "storefront",
			path: organizationPathPrefix.get() || "/",
			historyStrategy: "push",
		});
	}

	override render() {
		return html`
			<div class="flex items-center justify-between gap-4">
				<brand-logo
					named
					class="h-8 w-auto cursor-pointer"
					role="link"
					tabindex="0"
					aria-label=${when(
						openBrandName.get(),
						(brand) => this.t("storefront.brandBar.homeAriaLabel", { brand }),
						() => this.t("storefront.brandBar.homeAriaLabelUnnamed"),
					)}
					@click=${() => this.toStorefront()}
					@keydown=${(e: KeyboardEvent) => {
						// Enter/Space on a role-bearing non-button element, which the
						// browser does not activate for us.
						if (e.key !== "Enter" && e.key !== " ") return;
						e.preventDefault();
						this.toStorefront();
					}}
				></brand-logo>
				<div class="flex min-w-0 items-center gap-1">
					${when(
						hasOrders.value || orderHistory.value.status === "error",
						() => html`
							<div
								class="relative"
								${hummingbird({
									playing: this.flourish === "flying",
									waypoints: [
										{
											ref: "#orders-entry",
											label: this.t("storefront.orders.ticketsHere"),
										},
									],
									// Under the entry: the toggle sits beside it on the same row,
									// and the default 'top' would hang the caption off the header.
									labelPlacement: "bottom",
								})}
								@hummingbird-complete=${() => {
									this.flourish = "flown";
								}}
							>
								<schmancy-button
									id="orders-entry"
									variant="outlined"
									color="neutral"
									size="sm"
									compact
									title=${this.t("storefront.orders.entryLabel")}
									aria-label=${this.t("storefront.orders.entryLabel")}
									@click=${() =>
										area.push({
											area: "root",
											component: "orders",
											path: `${organizationPathPrefix.get()}/orders`,
											historyStrategy: "push",
										})}
								>
									<schmancy-icon slot="prefix">confirmation_number</schmancy-icon>
								</schmancy-button>
								${when(
									this.ticketsHere,
									() => html`
										<schmancy-visually-hidden role="status" aria-live="polite"
											>${this.t("storefront.orders.ticketsHere")}</schmancy-visually-hidden
										>
									`,
								)}
							</div>
						`,
					)}
					<language-toggle></language-toggle>
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"brand-bar": BrandBar;
	}
}
